import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import { Skeleton } from "@/components/base/Loading";
import ProductCard from "@/pages/products/components/ProductCard";
import ProductDetailModal from "@/pages/products/components/ProductDetailModal";
import { useSoftwareProducts, type SoftwareProduct } from "@/pages/products/hooks/useSoftwareProducts";

type SortKey = "recommend" | "rating" | "downloads" | "version";

const SORT_KEYS: { key: SortKey; labelKey: string }[] = [
  { key: "recommend", labelKey: "products.sortRecommend" },
  { key: "rating", labelKey: "products.sortRating" },
  { key: "downloads", labelKey: "products.sortDownloads" },
  { key: "version", labelKey: "products.sortVersion" },
];

const ALL = "__all__";

function versionParts(value: string | null | undefined): number[] {
  if (!value) return [];
  return value
    .replace(/^v/i, "")
    .split(".")
    .map((part) => parseInt(part, 10) || 0);
}

function compareVersion(a: string | null | undefined, b: string | null | undefined): number {
  const partsA = versionParts(a);
  const partsB = versionParts(b);
  const length = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < length; i += 1) {
    const diff = (partsB[i] ?? 0) - (partsA[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

export default function ProductsPage() {
  const { t } = useTranslation();
  const { products, loading, error, reload } = useSoftwareProducts();
  const [activeCategory, setActiveCategory] = useState<string>(ALL);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("recommend");
  const [selectedProduct, setSelectedProduct] = useState<SoftwareProduct | null>(null);

  const categories = useMemo(() => {
    const set = new Set<string>();
    products.forEach((product) => {
      if (product.category) set.add(product.category);
    });
    return [ALL, ...Array.from(set)];
  }, [products]);

  const visibleProducts = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    const filtered = products.filter((product) => {
      const matchCategory = activeCategory === ALL || product.category === activeCategory;
      if (!matchCategory) return false;
      if (!keyword) return true;
      const haystack = `${product.name} ${product.category ?? ""} ${product.summary ?? ""}`.toLowerCase();
      return haystack.includes(keyword);
    });

    const sorted = [...filtered];
    if (sortKey === "rating") {
      sorted.sort((a, b) => Number(b.rating) - Number(a.rating));
    } else if (sortKey === "downloads") {
      sorted.sort((a, b) => Number(b.downloads) - Number(a.downloads));
    } else if (sortKey === "version") {
      sorted.sort((a, b) => compareVersion(a.latest_version, b.latest_version));
    } else {
      sorted.sort((a, b) => a.sort_order - b.sort_order);
    }
    return sorted;
  }, [products, activeCategory, query, sortKey]);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
              <span className="flex h-3 w-3 items-center justify-center">
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              </span>
              {t("products.badge")}
            </span>
            <h1 className="mt-4 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
              {t("products.title")}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {t("products.subtitle")}
            </p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          {loading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2, 3, 4, 5].map((item) => (
                <div key={item} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <Skeleton className="h-11 w-11 rounded-md" />
                  <Skeleton className="mt-4 h-4 w-2/3" />
                  <Skeleton className="mt-3 h-3 w-full" />
                  <Skeleton className="mt-2 h-3 w-4/5" />
                  <Skeleton className="mt-5 h-8 w-full" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-error-warning-line text-xl" />
              </span>
              <p className="text-sm text-foreground-600">{error}</p>
              <button
                type="button"
                onClick={reload}
                className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("products.reload")}
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="relative w-full md:max-w-sm">
                  <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                    <i className="ri-search-line" />
                  </span>
                  <input
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={t("products.searchPlaceholder")}
                    className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-9 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={() => setQuery("")}
                      className="absolute right-2.5 top-1/2 flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
                      aria-label={t("products.clearSearch")}
                    >
                      <i className="ri-close-line" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <span className="hidden text-xs text-foreground-500 sm:inline">
                    {t("products.totalUnit", { count: visibleProducts.length })}
                  </span>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                      <i className="ri-sort-desc" />
                    </span>
                    <select
                      value={sortKey}
                      onChange={(event) => setSortKey(event.target.value as SortKey)}
                      className="cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-9 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                      aria-label={t("products.sortLabel")}
                    >
                      {SORT_KEYS.map((option) => (
                        <option key={option.key} value={option.key}>
                          {t(option.labelKey)}
                        </option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                      <i className="ri-arrow-down-s-line" />
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-6 mb-6 flex flex-wrap gap-2">
                {categories.map((category) => {
                  const active = category === activeCategory;
                  return (
                    <button
                      key={category}
                      type="button"
                      onClick={() => setActiveCategory(category)}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                        active
                          ? "bg-primary-500 text-background-50"
                          : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      {category === ALL ? t("products.categoryAll") : category}
                    </button>
                  );
                })}
              </div>

              {visibleProducts.length === 0 ? (
                <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-background-200 text-foreground-500">
                    <i className="ri-search-eye-line text-2xl" />
                  </span>
                  <p className="text-sm font-medium text-foreground-800">{t("products.emptyTitle")}</p>
                  <p className="text-xs text-foreground-500">{t("products.emptyDesc")}</p>
                  <button
                    type="button"
                    onClick={() => {
                      setQuery("");
                      setActiveCategory(ALL);
                    }}
                    className="mt-1 cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                  >
                    {t("products.resetFilter")}
                  </button>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {visibleProducts.map((product) => (
                    <ProductCard key={product.id} product={product} onOpenDetails={setSelectedProduct} />
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </main>

      {selectedProduct && (
        <ProductDetailModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
      )}

      <AppFooter />
    </div>
  );
}